// ========================= RentalForm.jsx =========================
import React, { useEffect, useState, useRef, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { useSelector, useDispatch } from "react-redux"
import { getCabs } from "../../../../../store/Cab/CabAction"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../../utils/placeDetails"

import { getDefaultOptions, getDefaultOptionsSync } from "../../../../../utils/defaultCityOptions"
import { saveRecentSearch, saveRecentTrip } from "../../../../../utils/recentSearches"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../../../utils/mobileNumberUtils"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../../components/DateTime"
import { geocodeCity } from "../../../../../utils/geocodeCity"

const makeCityOption = (cityName) => ({
  label: cityName,
  value: { description: `${cityName}, India`, place_id: null },
})

const RentalForm = ({ chipPrefill }) => {
  const [formValues, setFormValues] = useState({
    location: null,
    package: "",
    pickUpDate: "",
    pickUpTime: "",
    mobileNumber: "",
  })

  const extractCityFromText = (text = "") => (text.split(",")[0] || "").trim()

  const [defaultLocationOptions, setDefaultLocationOptions] = useState(() =>
    getDefaultOptionsSync("from")
  )
  const [locationMenuIsOpen, setLocationMenuIsOpen] = useState(false)

  const [locationDetails, setLocationDetails] = useState({
    lat: null,
    lng: null,
    fullAddress: "",
    city: "",
  })

  const routerLocation = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { location } = useSelector((state) => state.location)
  const [searchLoading, setSearchLoading] = useState(false)

  const locationSelectRef = useRef(null)
  const packageSelectRef = useRef(null)
  const mobileNumberInputRef = useRef(null)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)

  const locationInitializedRef = useRef(false)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")
  const [randomNumber] = useState(() => Math.floor(Math.random() * 60) + 1)

  const getLabel = (v) => {
    if (!v) return ""
    if (typeof v === "string") return v
    return v?.fullAddress || v?.label || ""
  }

  const dispatchTripDataUpdated = () => window.dispatchEvent(new Event("tripDataUpdated"))

  const resolveCityToPlaceOption = useCallback(
    (cityText) =>
      new Promise((resolve) => {
        if (!cityText) return resolve(null)
        resolve({ label: cityText, value: { description: cityText } })
      }),
    []
  )

  // suggestions
  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.location?.city || formValues?.location?.label
      const options = await getDefaultOptions("from", currentCity)
      setDefaultLocationOptions(options || [])
    }
    loadDefaultOptions()
  }, [location?.city, formValues?.location])

  // focus first
  useEffect(() => {
    if (!formValues.location) setTimeout(() => locationSelectRef.current?.focus?.(), 100)
  }, []) // eslint-disable-line

  // autofill location on home/cities
  useEffect(() => {
    locationInitializedRef.current = false

    const run = async () => {
      if (locationInitializedRef.current) return

      if (routerLocation.pathname === "/" && location?.city) {
        const option = await resolveCityToPlaceOption(location.city)
        if (option) {
          locationInitializedRef.current = true
          
          // Fetch coordinates for the city
          const geoDetails = await geocodeCity(location.city)
          
          const optionWithCoords = {
            ...option,
            latitude: geoDetails?.lat ?? null,
            longitude: geoDetails?.lng ?? null,
            city: geoDetails?.city || location.city,
            fullAddress: geoDetails?.fullAddress || location.city,
          }
          
          setFormValues((prev) => ({ ...prev, location: optionWithCoords }))
          if (geoDetails) {
            setLocationDetails({
              lat: geoDetails.lat,
              lng: geoDetails.lng,
              fullAddress: geoDetails.fullAddress,
              city: geoDetails.city,
            })
          }
          setTimeout(() => packageSelectRef.current?.focus?.(), 200)
        }
        return
      }

      if (routerLocation.pathname?.startsWith("/cities/")) {
        const prefill = localStorage.getItem("pulpit_prefill_from_city")
        if (prefill) {
          const option = await resolveCityToPlaceOption(prefill)
          if (option) {
            locationInitializedRef.current = true
            
            // Fetch coordinates for the city
            const geoDetails = await geocodeCity(prefill)
            
            const optionWithCoords = {
              ...option,
              latitude: geoDetails?.lat ?? null,
              longitude: geoDetails?.lng ?? null,
              city: geoDetails?.city || prefill,
              fullAddress: geoDetails?.fullAddress || prefill,
            }
            
            setFormValues((prev) => ({ ...prev, location: optionWithCoords }))
            if (geoDetails) {
              setLocationDetails({
                lat: geoDetails.lat,
                lng: geoDetails.lng,
                fullAddress: geoDetails.fullAddress,
                city: geoDetails.city,
              })
            }
            setTimeout(() => packageSelectRef.current?.focus?.(), 200)
          }
        }
      }
    }

    run()
  }, [routerLocation.pathname, location?.city, resolveCityToPlaceOption])

  // Chip prefill (recent search chip clicked)
  useEffect(() => {
    if (!chipPrefill || chipPrefill.trip_type !== 2) return
    setFormValues((prev) => ({
      ...prev,
      location: makeCityOption(chipPrefill.fromCity),
      package: chipPrefill.package || "",
    }))
  }, [chipPrefill])

  const handlePlaceSelect = async (value) => {
    if (!value) {
      setFormValues((prev) => ({ ...prev, location: null }))
      setLocationDetails({ lat: null, lng: null, fullAddress: "", city: "" })
      return
    }

    const placeId = value?.value?.place_id || value?.place_id
    const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
    const labelText = getLabel(value)
    const nextValue = {
      ...value,
      latitude: details?.lat ?? null,
      longitude: details?.lng ?? null,
      city: details?.city || extractCityFromText(labelText),
      fullAddress: details?.fullAddress || labelText,
    }

    setFormValues((prev) => ({ ...prev, location: nextValue }))
    setLocationDetails(details || {})

    saveRecentSearch(nextValue, "from")

    setLocationMenuIsOpen(false)
    setTimeout(() => packageSelectRef.current?.focus?.(), 120)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))

    if (name === "package" && value) {
      setTimeout(() => pickUpDateRef.current?.open?.(), 150)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formValues.location) {
      return toast.error("Please select location")
    }
    if (!formValues.package) {
      return toast.error("Please select package")
    }
    if (!formValues.pickUpDate) {
      return toast.error("Please select pickup date")
    }
    if (!formValues.pickUpTime) {
      return toast.error("Please select pickup time")
    }
    if (!formValues.mobileNumber) {
      return toast.error("Please enter mobile number")
    }
    if (!isValidMobileNumber(formValues.mobileNumber)) {
      return toast.error("Enter a valid 10-digit mobile number (cannot start with 0)")
    }


    // Try to get coordinates if missing
    let lat = formValues.location.latitude ?? null
    let lng = formValues.location.longitude ?? null
    let city = formValues.location.city || extractCityFromText(getLabel(formValues.location)) || ""
    let fullAddress = formValues.location.fullAddress || formValues.location.label || ""

    if (lat === null || lng === null) {
      const geoDetails = await geocodeCity(formValues.location.label || fullAddress)
      if (geoDetails) {
        lat = geoDetails.lat
        lng = geoDetails.lng
        city = city || geoDetails.city
        fullAddress = fullAddress || geoDetails.fullAddress
      }
    }

    if (!city) {
      return toast.error("Please select a valid location")
    }

    const tripData = {
      trip_type: 2,
      round_trip: 0,
      pickup: fullAddress,
      drop: "",
      pickup_lat: lat ?? null,
      pickup_long: lng ?? null,
      drop_lat: null,
      drop_long: null,
      pickup_city: city,
      drop_city: "",
      package_type_in_hours: parseInt(formValues.package, 10),
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: formValues.mobileNumber,
      ip_address: location?.ip || "8425",
    }

    setSearchLoading(true)
    const safetyTimeout = setTimeout(() => {
      setSearchLoading(false)
    }, 15000) // 15s safety

    try {
      const response = await dispatch(getCabs(tripData)).unwrap()
      clearTimeout(safetyTimeout)
      localStorage.setItem("tripData", JSON.stringify(tripData))
      localStorage.setItem("cabSearchResult", JSON.stringify(response?.data ?? {}))
      dispatchTripDataUpdated()

      const buildQueryParam = (k, v) => {
        if (v === null || v === undefined || v === "") return ""
        return `${k}=${encodeURIComponent(v)}`
      }

      const queryParamsArray = [
        `trip_type=${tripData.trip_type}`,
        `round_trip=${tripData.round_trip}`,
        buildQueryParam("pickup", tripData.pickup),
        buildQueryParam("pickup_lat", tripData.pickup_lat),
        buildQueryParam("pickup_long", tripData.pickup_long),
        buildQueryParam("pickup_city", tripData.pickup_city),
        buildQueryParam("package_type_in_hours", tripData.package_type_in_hours),
        buildQueryParam("trip_date", tripData.trip_date),
        buildQueryParam("trip_time", tripData.trip_time),
        buildQueryParam("phone_number", tripData.phone_number),
      ].filter(Boolean)

      saveRecentTrip({ trip_type: 2, fromCity: city, package: formValues.package })
      navigate(`/cabs?${queryParamsArray.join("&")}`)
    } catch (err) {
      console.error("RentalForm search failed:", err)
      const msg = err && typeof err === "string" ? err : (err?.message || "Something went wrong")
      const isRateLimit =
        typeof msg === "string" &&
        (msg.includes("Rate limit") || msg.includes("Maximum"))
      toast.error(
        isRateLimit
          ? "You've exceeded the request limit. Please try again after a few hours."
          : msg
      )
    } finally {
      setSearchLoading(false)
    }
  }

  return (
    <div className="rental-form">
      <form onSubmit={handleSubmit}>
        <GooglePlacesAutocomplete

          debounce={400}
          minLengthAutocomplete={3}
          selectProps={{
            ref: locationSelectRef,
            value: formValues.location,
            onChange: handlePlaceSelect,
            placeholder: "Location",
            isClearable: true,

            defaultOptions: defaultLocationOptions,
            menuIsOpen: locationMenuIsOpen,
            onFocus: () => setLocationMenuIsOpen(true),
            onBlur: () => setLocationMenuIsOpen(false),
            onMenuClose: () => setLocationMenuIsOpen(false),
            onInputChange: () => setLocationMenuIsOpen(true),

            getOptionLabel: (e) => e?.label || "",
            getOptionValue: (e) =>
              e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",

            styles: {
              control: (p, state) => ({
                ...p,
                padding: "0 6px",
                border: state.isFocused ? "1.5px solid #21a5a0" : "1.5px solid #e6ecea",
                borderRadius: "1.2rem",
                background: state.isFocused ? "#ffffff" : "#f5f9f8",
                boxShadow: state.isFocused ? "0 0 0 4px #e9f6f5" : "none",
                width: "100%",
                height: "4.4rem",
                minHeight: "4.4rem",
                display: "flex",
                alignItems: "center",
                marginBottom: "1.4rem",
                fontSize: "1.4rem",
                transition: "border-color .18s, box-shadow .18s, background .18s",
              }),
              input: (p) => ({ ...p, color: "#152321", fontSize: "1.4rem" }),
              placeholder: (p) => ({ ...p, color: "#71817f", fontSize: "1.4rem" }),
              menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3, borderRadius: "1.2rem", overflow: "hidden" }),
            },
            components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
          }}
          autocompletionRequest={{ componentRestrictions: { country: "in" } }}
        />

        <select
          className="input-select"
          name="package"
          value={formValues.package}
          onChange={handleChange}
          ref={packageSelectRef}
          required
        >
          <option value="" disabled>
            Select Package
          </option>
          <option value="4">4Hours - 40 km</option>
          <option value="6">6Hours - 60 km</option>
          <option value="8">8Hours - 80km</option>
          <option value="12">12Hours - 120km</option>
        </select>

        <DateTimeProvider>
          <div className="rental-form__date-time mb-5">
            <DatePickerInput
              ref={pickUpDateRef}
              value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              minDate={new Date(formattedToday)}
              placeholder="Pick Up Date"
              onChange={(d) => {
                setFormValues((prev) => ({
                  ...prev,
                  pickUpDate: d ? new Date(d).toLocaleDateString("en-CA") : "",
                }))
                setTimeout(() => pickUpTimeRef.current?.open?.(), 200)
              }}
            />

            <TimePickerInput
              ref={pickUpTimeRef}
              value={formValues.pickUpTime}
              placeholder="Pick Up Time"
              selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              onChange={(t) => {
                setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))
                setTimeout(() => mobileNumberInputRef.current?.focus?.(), 150)
              }}
            />
          </div>
        </DateTimeProvider>

        <div className="input-phone-wrapper mb-5">
          <div
            className={`input-phone-box${isValidMobileNumber(formValues.mobileNumber) ? " input-phone-box--valid" : formValues.mobileNumber.length > 0 ? " input-phone-box--invalid" : ""}`}
          >
            <span className="phone-code">+91</span>
            <span className="phone-divider"></span>

            <input
              type="tel"
              className="phone-input"
              placeholder="Mobile Number"
              name="mobileNumber"
              value={formatMobileDisplay(formValues.mobileNumber)}
              onChange={(e) => {
                const v = parseMobileInput(e.target.value)
                setFormValues((p) => ({ ...p, mobileNumber: v }))
              }}
              maxLength={11}
              ref={mobileNumberInputRef}
              required
            />
          </div>
        </div>

        <button type="submit" className="input-submit-button" disabled={searchLoading}>
          {searchLoading ? "Searching..." : "Search"}
        </button>
      </form>

      <p className="rental-form__paragraph">
        Last cab booked from {location?.city || "Ahmedabad"} {randomNumber} sec ago.
      </p>
    </div>
  )
}

export default RentalForm
